import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
	combineAll,
	concatenateAll,
	isSame,
	UuidService,
	WINDOW,
} from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { OrderedSet } from 'immutable';
import {
	catchError,
	delay,
	exhaustMap,
	filter,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { MetadataAdapter } from '../../core/adapters/metadata.adapter';
import { TagAdapter } from '../../core/adapters/tag.adapter';
import { states } from '../../core/application.states';
import { setGloballyAvailableTags, TagSelector } from '../../core/store';
import { isCurrentPage } from '../../core/utils/is-current-page';
import { replaceMultipleForwardSlash } from '../../core/utils/replace-multiple-forward-slash';
import {
	CharsetRequester,
	ConnectorRequester,
	ExportTemplateRequester,
} from '../../requesters';
import { TagRequester } from '../../requesters';
import { IDcTag } from '../../requesters';
import { IDcMetadataResponse } from '../../requesters/core/models/metadata.model';
import { FilenameLanguageTranslationRequester } from '../../requesters/export-template/filename-language-translation.requester';
import { ExportDatablockModalComponent } from '../components/export-datablock-modal/export-datablock-modal.component';
import { FileNameGenStrategy } from '../components/export-file-configuration/export-file-configuration.entity';
import { ExportTemplateDeleteConfirmationComponent } from '../components/export-template-delete-confirmation/export-template-delete-confirmation.component';
import { ExportTemplateAdapter } from '../export-template.adapter';
import {
	closeExecuteExportUsingTemplateModal,
	configureSelectedDatablockExport,
	createExportTemplateConfig,
	createExportTemplateConfigFailure,
	createExportTemplateConfigSuccess,
	deleteExportTemplatesById,
	deleteExportTemplatesByIdSuccess,
	executeExportUsingTemplate,
	exportExecutionFinished,
	fetchAvailableCharsetsFailure,
	fetchAvailableCharsetsSuccess,
	fetchAvailableConnectorsSuccess,
	fetchExportConfigFailure,
	fetchExportConfigSuccess,
	fetchExportTemplateConfig,
	fetchExportTemplateList,
	fetchExportTemplateListFailure,
	fetchExportTemplateListSuccess,
	fetchSelectedExportTemplateConfig,
	getAllAvailableTemplates,
	goToExportTemplateListView,
	initExportDatablockUsingExistingTemplate,
	initExportTemplateConfig,
	initExportTemplateList,
	navigateToExportTemplateCreate,
	navigateToExportTemplateEdit,
	refreshExportTemplateList,
	resetExportTemplate,
	searchAvailableExportTemplate,
	toggleExportStatus,
	updateExportTemplateConfig,
	predictNormalizedDatablockLabel,
	predictNormalizedDatablockLabelSuccess,
	predictNormalizedDatablockLabelFailure,
} from './export-template.actions';
import { ExportTemplateSelector } from './export-template.selector';

interface IExportJobSocketResponse {
	data: { status: string; client_id: string } | undefined;
}

@Injectable()
export class ExportTemplateEffects {
	public charsets$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				initExportTemplateConfig,
				initExportDatablockUsingExistingTemplate
			),
			switchMap(() => this.charsetRequester.getAvailableCharsets()),
			map((charsets) =>
				fetchAvailableCharsetsSuccess({
					charsets: this.exportTemplateParser.generateCharsets(charsets),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchAvailableCharsetsFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public connectors$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				initExportTemplateConfig,
				initExportDatablockUsingExistingTemplate
			),
			switchMap(() =>
				this.connectorRequester.getAvailableConnectors(['HTTP'], ['READ_ONLY'])
			),
			map((res) => this.exportTemplateParser.generateConnectorsList(res)),
			map((list) =>
				fetchAvailableConnectorsSuccess({
					connectors: list,
				})
			)
		)
	);

	public triggerGetTags$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createExportTemplateConfig, updateExportTemplateConfig),
			withLatestFrom(this.tagSelector.getTags$()),
			delay(1000),
			switchMap(([action, tags]) => {
				const hasNewTags = action.payload.templateTags.some((t) => t.id === -1);
				if (hasNewTags) {
					return combineAll({
						tags: this.tagRequester.getAvailableTags(true),
						shouldRemap: true,
					});
				}
				const hasSameTagsLabels = isSame(
					action.payload.templateTags,
					tags.toArray(),
					'label'
				);
				const hasSameColors = isSame(
					action.payload.templateTags,
					tags.toArray(),
					'color'
				);
				if (hasNewTags || !hasSameTagsLabels || !hasSameColors) {
					return combineAll({
						tags: this.tagRequester.getAvailableTags(true),
						shouldRemap: true,
					});
				}
				return combineAll({
					tags,
					shouldRemap: false,
				});
			}),
			map((res) =>
				res.shouldRemap
					? this.tagAdapter.generateTags(res.tags as ReadonlyArray<IDcTag>)
					: (res.tags as OrderedSet<AppTagItemEntity>)
			),
			map((tags) =>
				setGloballyAvailableTags({
					availableTags: tags,
				})
			)
		)
	);

	public tags$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initExportTemplateConfig),
			switchMap(() => this.tagRequester.getAvailableTags(true)),
			map((res) => this.tagAdapter.generateTags(res)),
			map((tags) =>
				setGloballyAvailableTags({
					availableTags: tags,
				})
			)
		)
	);

	public exportTemplateList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fetchExportTemplateList, deleteExportTemplatesByIdSuccess),
			switchMap((action) =>
				this.exportTemplateRequester
					.getAvailableTemplates(action.excludeInactive)
					.pipe(
						map((response) =>
							fetchExportTemplateListSuccess({
								list: this.exportTemplateParser.generateList(response),
							})
						),
						catchError((error: HttpErrorResponse) => {
							console.error(error);
							return of(
								fetchExportTemplateListFailure({
									error,
								})
							);
						})
					)
			)
		)
	);

	public exportTemplateListBroadcast$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fetchExportTemplateListSuccess),
				tap(() =>
					this.ajsScopeService.broadcastOnRootScope(
						'export-template:list:refresh'
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public availableExportTemplateList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAllAvailableTemplates),
			switchMap(() =>
				this.exportTemplateRequester.getAvailableSearchableTemplates().pipe(
					map((response) =>
						fetchExportTemplateListSuccess({
							list: this.exportTemplateParser.generateList(response),
						})
					),
					catchError((error: HttpErrorResponse) => {
						console.error(error);
						return of(
							fetchExportTemplateListFailure({
								error,
							})
						);
					})
				)
			)
		)
	);

	public exportTemplateListBySearchTerm$ = createEffect(() =>
		this.actions$.pipe(
			ofType(searchAvailableExportTemplate),
			switchMap((action) =>
				combineAll({
					shouldExcludeInactive: true,
					response: this.exportTemplateRequester.searchTemplatesByTerm(
						action.searchTerm
					),
				})
			),
			map((result) =>
				this.exportTemplateParser.generateList(
					result.response,
					result.shouldExcludeInactive
				)
			),
			map((payload) =>
				fetchExportTemplateListSuccess({
					list: payload,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchExportTemplateListFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public exportTemplateConfig$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fetchExportTemplateConfig, fetchSelectedExportTemplateConfig),
			withLatestFrom(this.exportTemplateSelector.getSelectedDatablock$()),
			switchMap(([action, targetDatablockId]) =>
				combineAll({
					exportTemplate: this.exportTemplateRequester.getById(action.exportId),
					datablockId: of(targetDatablockId ? targetDatablockId : undefined),
				})
			),
			map(({ exportTemplate, datablockId }) => ({
				exportTemplate:
					this.exportTemplateParser.generateExportTemplate(exportTemplate),
				metadata: this.metadataAdapter.generateMetadata(
					exportTemplate.metadata
				),
				datablockId,
			})),
			switchMap(({ exportTemplate, metadata, datablockId }) => {
				const extensionParams = {
					active: exportTemplate.fileNameConfig.shouldApplyCustomExtension,
					value: exportTemplate.fileNameConfig.customExtension,
					format: exportTemplate.outputType,
				};

				if (
					exportTemplate.fileNameConfig.strategy ===
						FileNameGenStrategy.UserDefinedFileNamePattern &&
					exportTemplate.fileNameConfig.userDefinedLabel !== undefined
				) {
					return combineAll({
						exportTemplate: of(exportTemplate),
						metadata: of(metadata),
						predicted:
							this.filenameLanguageTranslationRequester.predictOutputFileNameWithExtension(
								[
									exportTemplate.path,
									exportTemplate.fileNameConfig.userDefinedLabel as string,
								],
								datablockId,
								extensionParams
							),
					});
				}

				return combineAll({
					exportTemplate: of(exportTemplate),
					metadata: of(metadata),
					predicted:
						this.filenameLanguageTranslationRequester.predictOutputFileNameWithExtension(
							[exportTemplate.path, '%uuid'],
							undefined,
							extensionParams
						),
				});
			}),
			map(({ exportTemplate, metadata, predicted }) =>
				fetchExportConfigSuccess({
					entity: exportTemplate.setPredictedFileName(
						replaceMultipleForwardSlash(concatenateAll(predicted, '/'))
					),
					metadata,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchExportConfigFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public exportTemplateSave$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createExportTemplateConfig, updateExportTemplateConfig),
			exhaustMap((action) => {
				if (action.payload.templateDetails.id === -1) {
					return combineAll({
						isNew: true,
						response:
							this.exportTemplateRequester.createExportTemplateConfiguration(
								action.payload
							),
					});
				}
				return combineAll({
					isNew: false,
					response:
						this.exportTemplateRequester.updateExportTemplateConfiguration(
							action.payload.templateDetails.id,
							action.payload
						),
				});
			}),
			map((result) => ({
				isNew: result.isNew,
				details: this.exportTemplateParser.parseTemplateBasicDetails(
					result.response
				),
			})),
			tap((result) => {
				const msg = result.isNew
					? $localize`:i18n=@@export.save.success:`
					: $localize`:i18n=@@export.save.update:`;
				this.ngCommService.notifyOnSuccess(msg);
			}),
			map((result) =>
				createExportTemplateConfigSuccess({
					templateId: result.details.id,
					exportType: result.details.type,
					isNew: result.isNew,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				this.ngCommService.notifyOnError($localize`:i18n=@@export.save.error:`);
				this.store.dispatch(
					createExportTemplateConfigFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public exportTemplateRedirectToEdit$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createExportTemplateConfigSuccess),
				filter((action) => action.isNew),
				tap((action) =>
					this.stateService.go(states.ExportTemplateEdit, {
						exportConfigId: `${action.templateId}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public exportTemplateReloadAfterUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createExportTemplateConfigSuccess),
			filter((action) => !action.isNew),
			map((action) =>
				fetchExportTemplateConfig({
					exportId: action.templateId,
				})
			)
		)
	);

	public deleteExportTemplates$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteExportTemplatesById),
			map((action) => ({
				action,
				ref: this.modalService.openPopup<unknown>(
					ExportTemplateDeleteConfirmationComponent,
					{
						title: $localize`:i18n=@@export.delete.modal.title:`,
						subTitle: $localize`:i18n=@@export.delete.modal.subTitle:`,
					}
				),
			})),
			switchMap((result) =>
				combineAll({
					action: result.action,
					shouldContinue: result.ref.afterClosed(),
				})
			),
			filter((result) => !!result.shouldContinue.action),
			exhaustMap((result) =>
				combineAll({
					action: result.action,
					response: this.exportTemplateRequester.deleteByIds(
						result.action.elemIds
					),
				})
			),
			tap((result) =>
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@export.delete.success:`
				)
			),
			map((result) =>
				deleteExportTemplatesByIdSuccess({
					excludeInactive: result.action.excludeInactive,
				})
			)
		)
	);

	public exportTemplateRedirectToList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(goToExportTemplateListView),
			tap(() => this.stateService.go(states.ExportTemplateList)),
			map(() => resetExportTemplate())
		)
	);

	public configureSelectedDatablockExport$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(configureSelectedDatablockExport),
				map((action) => {
					const subTitle =
						$localize`:i18n=@@datablock.list.export.modal.subTitle:` +
						' ' +
						action.datablockLabel;
					return this.modalService.openModal<{ datablockId: number }>(
						ExportDatablockModalComponent,
						{
							title: $localize`:i18n=@@datablock.list.export.modal.title:`,
							subTitle,
							datablockId: action.datablockId,
						}
					);
				}),
				switchMap((modalRef) => modalRef.afterClosed())
			),
		{
			dispatch: false,
		}
	);

	public executeExportUsingTemplate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(executeExportUsingTemplate),
			withLatestFrom(this.exportTemplateSelector.getSelectedDatablock$()),
			tap(() =>
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@datablock.export.modal.executing:`
				)
			),
			exhaustMap(([action, targetDatablockId]) => {
				if (targetDatablockId === undefined) {
					throw new Error('no datablock selected to execute export');
				}
				const clientUuid = this.uuidService.uuidV4();
				return combineAll({
					clientId: clientUuid,
					exportHistoryId: this.exportTemplateRequester.execute(
						clientUuid,
						targetDatablockId,
						action.exportTemplate
					),
				});
			}),
			switchMap((payload) =>
				combineAll({
					payload,
					stompResponse:
						this.ajsScopeService.listenOnRootScope<IExportJobSocketResponse>(
							'exportJobFinished'
						),
				})
			),
			filter(
				(obj) =>
					obj.stompResponse?.data?.status.toLocaleLowerCase() !== 'running' &&
					obj.stompResponse?.data?.client_id === obj.payload.clientId
			),
			tap((obj) => {
				if (
					(obj.stompResponse?.data?.status.toLocaleLowerCase() === 'success' ||
						obj.stompResponse?.data?.status.toLocaleLowerCase() === 'cached') &&
					obj.stompResponse.data.client_id === obj.payload.clientId
				) {
					this.modalService.closeActiveModal();
					this.ajsScopeService.broadcastOnRootScope(
						'exportJobFinished:success:to-ajs',
						obj.stompResponse
					);
				} else if (
					obj.stompResponse?.data?.status.toLocaleLowerCase() === 'error'
				) {
					throw new Error(
						'error during export execution using selected export template'
					);
				}
			}),
			map(() => exportExecutionFinished()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@datablock.export.modal.executing.error:`
				);
				this.store.dispatch(exportExecutionFinished());
				return caught;
			})
		)
	);

	public exportDatablockModalClose$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(closeExecuteExportUsingTemplateModal, exportExecutionFinished),
				tap(() => {
					if (isCurrentPage('datablocks', 'edit')) {
						this.stateService.go(states.DatablockEdit);
						return;
					}
					this.stateService.go(states.DatablockList);
				})
			),
		{
			dispatch: false,
		}
	);

	public initExportTemplateList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initExportTemplateList),
			switchMap(() => this.exportTemplateRequester.getExportTemplateList(true)),
			map((response) => this.exportTemplateParser.generateList(response)),
			map((payload) =>
				fetchExportTemplateListSuccess({
					list: payload,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchExportTemplateListFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshExportTemplateList$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshExportTemplateList),
			switchMap(() =>
				this.exportTemplateRequester.getExportTemplateList(false)
			),
			map((response) => this.exportTemplateParser.generateList(response)),
			map((payload) =>
				fetchExportTemplateListSuccess({
					list: payload,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchExportTemplateListFailure({
						error,
					})
				);
				return caught;
			})
		)
	);

	public navigateToCreateRoute$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToExportTemplateCreate),
				tap(() =>
					this.stateService.go(
						states.ExportTemplateEdit,
						{
							exportConfigId: '-1',
							type: 'new',
						},
						true
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToEditRoute$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToExportTemplateEdit),
				tap((action) =>
					this.stateService.go(
						states.ExportTemplateEdit,
						{
							exportConfigId: `${action.exportConfigId}`,
						},
						true
					)
				)
			),
		{
			dispatch: false,
		}
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.actions$.pipe(
			ofType(toggleExportStatus),
			switchMap((action) =>
				this.exportTemplateRequester.getById(action.exportId)
			),
			switchMap((exportTemplate) =>
				this.exportTemplateRequester.updateExportTemplateConfiguration(
					exportTemplate.id ?? 1,
					{
						templateDetails:
							this.exportTemplateParser.generateExportTemplate(exportTemplate),
						templateMetadata: this.metadataAdapter.generateMetadata({
							...exportTemplate.metadata,
							actif: !exportTemplate.metadata?.actif,
						} as IDcMetadataResponse),
						templateTags: this.tagAdapter
							.generateResponseMetadataTag(exportTemplate.metadata?.tags ?? [])
							.toArray(),
					}
				)
			),
			map(() => refreshExportTemplateList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshExportTemplateList());
				return caught;
			})
		)
	);

	public predictNormalizedLabel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(predictNormalizedDatablockLabel),
			switchMap((action) =>
				this.exportTemplateRequester
					.getNormalizedDatablockLabel(action.datablockId, action.connectorId)
					.pipe(
						map((response) =>
							predictNormalizedDatablockLabelSuccess({
								normalizedLabel: response,
							})
						),
						catchError((error) => {
							console.error(error);
							// Return failure action or empty string success depending on desired error handling
							return of(
								predictNormalizedDatablockLabelFailure({
									error,
								})
							);
						})
					)
			)
		)
	);

	public constructor(
		@Inject(WINDOW) private readonly window: Window,
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly uuidService: UuidService,
		private readonly filenameLanguageTranslationRequester: FilenameLanguageTranslationRequester,
		private readonly exportTemplateRequester: ExportTemplateRequester,
		private readonly exportTemplateParser: ExportTemplateAdapter,
		private readonly exportTemplateSelector: ExportTemplateSelector,
		private readonly tagSelector: TagSelector,
		private readonly charsetRequester: CharsetRequester,
		private readonly connectorRequester: ConnectorRequester,
		private readonly tagRequester: TagRequester,
		private readonly ngCommService: NgCommService,
		private readonly stateService: AjsStateService,
		private readonly metadataAdapter: MetadataAdapter,
		private readonly tagAdapter: TagAdapter,
		private readonly ajsScopeService: AjsScopeService,
		private readonly modalService: AppModalService
	) {}
}
